import dayjs from 'dayjs';
import './enquireCard.css';
import { MdOutlineAccessTime, MdEvent, MdPerson, MdPhone } from 'react-icons/md';
import { useState } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import RequestLeadDeal from '../RequestLeadDeal';
import { FaRupeeSign } from 'react-icons/fa';

const EnquiryCard = ({ item, sno, onUpdate }) => {
    const { event, name, mobno, date, created_at, payment_status, platform_fee } = item;
    const [showDetailsModal, setShowDetailsModal] = useState(false);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount || 0);
    };

    return (
        <div className="col-md-6 col-lg-4">
            <div className="enquiry-card">
                <div className="enquiry-card-header">
                    <div className="enquiry-badge">
                        <span className="sno-badge">#{sno}</span>
                        {payment_status && (
                            <span className="status-badge closed">Deal Closed</span>
                        )}
                    </div>
                    <div className="event-type">
                        <MdEvent className="me-1" />
                        <span>{event || 'Event'}</span>
                    </div>
                </div>

                <div className="enquiry-card-body">
                    <div className="customer-info">
                        <div className="info-item">
                            <MdPerson className="info-icon" />
                            <div className="info-content">
                                <small className="text-muted">Customer</small>
                                <h6 className="mb-0">{name}</h6>
                            </div>
                        </div>
                        <div className="info-item">
                            <MdPhone className="info-icon" />
                            <div className="info-content">
                                <small className="text-muted">Contact</small>
                                <h6 className="mb-0">
                                    {mobno}
                                </h6>
                            </div>
                        </div>
                    </div>

                    <div className="event-details">
                        <div className="detail-item">
                            <span className="label">Event Date</span>
                            <span className="value">{dayjs(date).format('DD MMM, YYYY')}</span>
                        </div>
                        <div className="detail-item">
                            <span className="label">Platform Fee</span>
                            <span className="value">{platform_fee || 10}%</span>
                        </div>
                    </div>

                    <div className="timestamp">
                        <MdOutlineAccessTime size={14} className="me-1" />
                        <span>{dayjs(created_at).format('DD MMM, YYYY h:mm A')}</span>
                    </div>
                </div>

                <div className="enquiry-card-footer">
                    {payment_status ? (
                        <Button
                            variant="outline-primary"
                            size="sm"
                            className="view-details-btn"
                            onClick={() => setShowDetailsModal(true)}
                        >
                            View Details
                        </Button>
                    ) : (
                        <RequestLeadDeal 
                            item={item} 
                            dealCloseTrigger={onUpdate}
                        />
                    )}
                </div>
            </div>
            <Modal 
                show={showDetailsModal} 
                onHide={() => setShowDetailsModal(false)} 
                centered
                size="lg"
            >
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="text-primary fw-bold">
                        Deal Details
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-3">
                    <div className="deal-details-container">
                        {/* Customer Information */}
                        <div className="details-section">
                            <h6 className="section-title">Customer Information</h6>
                            <div className="details-grid">
                                <div className="detail-row">
                                    <span className="label">Name:</span>
                                    <span className="value">{name}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="label">Mobile:</span>
                                    <span className="value">{mobno}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="label">Event Type:</span>
                                    <span className="value">{event}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="label">Event Date:</span>
                                    <span className="value">{dayjs(date).format('DD MMMM, YYYY')}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="label">Enquiry Date:</span>
                                    <span className="value">{dayjs(created_at).format('DD MMM, YYYY h:mm A')}</span>
                                </div>
                            </div>
                        </div>

                        {/* Deal Information */}
                        {item.payment_status && (
                            <div className="details-section">
                                <h6 className="section-title">Deal Information</h6>
                                <div className="deal-summary">
                                    <div className="summary-item">
                                        <span className="label text-dark">Deal Close Price</span>
                                        <span className="value amount">
                                            <FaRupeeSign size={14} />
                                            {formatCurrency(item.deal_close_price)}
                                        </span>
                                    </div>
                                    <div className="summary-item">
                                        <span className="label text-dark">Platform Fee ({platform_fee}%)</span>
                                        <span className="value amount">
                                            <FaRupeeSign size={14} />
                                            {formatCurrency(item.payable_amount)}
                                        </span>
                                    </div>
                                    <div className="summary-item">
                                        <span className="label text-dark">GST (18%)</span>
                                        <span className="value amount">
                                            <FaRupeeSign size={14} />
                                            {formatCurrency(item.gst)}
                                        </span>
                                    </div>
                                    <div className="summary-item total">
                                        <span className="label text-dark">Total Payable</span>
                                        <span className="value amount fw-bold text-primary">
                                            <FaRupeeSign size={16} />
                                            {formatCurrency(item.total_amount)}
                                        </span>
                                    </div>
                                </div>
                                {item.remark && (
                                    <div className="remark-box">
                                        <small className="text-muted d-block mb-1">Remark</small>
                                        <p className="mb-0">{item.remark}</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Payment Information */}
                        <div className="details-section">
                            <h6 className="section-title">Payment Information</h6>
                            <div className="payment-status-info">
                                <div className={`status-badge-lg ${payment_status ? 'success' : 'pending'}`}>
                                    {payment_status ? 'Payment Completed' : 'Payment Pending'}
                                </div>
                                {item.transaction_id && (
                                    <div className="transaction-id mt-2">
                                        <small className="text-muted">Transaction ID:</small>
                                        <p className="mb-0 font-monospace">{item.transaction_id}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0">
                    <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};
export default EnquiryCard;